import React from 'react';
import PDFIMG from "../../../assets/pdf.png";
import { MenuItem } from "@mui/material";
import jsPDF from 'jspdf';
import { renderToString } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';

const PDFDownloadButton: React.FC<{ content: any }> = ({ content }) => {
  const handleDownload = () => {
    const doc = new jsPDF();
    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth() - margin * 2;
    const pageHeight = doc.internal.pageSize.getHeight() - margin * 2;

    // Convert Markdown to HTML
    const htmlContent: string | null = renderToString(<ReactMarkdown>{content}</ReactMarkdown>);
    if (!htmlContent) return; // Handle potential null value

    // Styles
    const listItemStyle = { fontSize: 12, marginBottom: 5 };
    const headingSpacing = 10;
    const listItemSpacing = 5;
    const paragraphSpacing = 10;
    const lineSpacing = 5;

    doc.setFont("Arial");
    doc.setFontSize(14);
    doc.setTextColor("#333");

    // Calculate the maximum height available for the content
    const maxHeight = pageHeight - 20; // Subtracting additional margin
    const maxWidth = pageWidth - 20; // Subtracting additional margin

    const parser = new DOMParser();
    const parsedContent = parser.parseFromString(htmlContent, 'text/html');

    let currentY = margin + 5; // Initial Y position

    const elements = parsedContent.body.children;
    for (const element of Array.from(elements)) {
      const tagName = element.tagName.toLowerCase();

      if (currentY + 20 > maxHeight) { // Check if the content exceeds the current page
        doc.addPage(); // Move to the next page
        currentY = margin + 5; // Reset Y position
      }

      if (tagName === 'strong') {
        doc.setFont('Arial', 'bold');
        const textLines = doc.splitTextToSize(element.textContent || '', maxWidth);
        textLines.forEach((line: string | string[]) => {
          doc.text(line, margin, currentY);
          currentY += headingSpacing;
        });
        doc.setFont('Arial', 'normal');
      } else if (tagName === 'ul') {
        const items = element.querySelectorAll('li');
        let firstItem = true; // To control the margin before the first <li> item
        items.forEach((item, index) => {
          const textLines = doc.splitTextToSize(`• ${item.textContent}`, maxWidth);
          textLines.forEach((line: string | string[]) => {
            if (!firstItem) {
              currentY += listItemSpacing; // Add spacing before the list item (except for the first item)
            }
            if (currentY + listItemSpacing + listItemStyle.marginBottom + 20 > maxHeight) { // Check if adding the list item would exceed the current page
              doc.addPage(); // Move to the next page
              currentY = margin + 5; // Reset Y position
            }
            doc.text(line, margin + 10, currentY);
            currentY += listItemStyle.marginBottom; // Add margin bottom for the list item
            firstItem = false;
          });
        });
        currentY += listItemSpacing; // Add spacing after the last list item
      } else {
        const textLines = doc.splitTextToSize(element.textContent || '', maxWidth);
        textLines.forEach((line: string | string[]) => {
          if (currentY + 20 > maxHeight) { // Check if the content exceeds the current page
            doc.addPage(); // Move to the next page
            currentY = margin + 5; // Reset Y position
          }
          doc.text(line, margin, currentY);
          currentY += lineSpacing;
        });
        currentY += paragraphSpacing;
      }
    }

    doc.save('document.pdf'); // Download the PDF
  };

  const iconStyle = {
    width: '20px',
    height: '20px',
    marginRight: '10px',
    fontSize: '14px'
  };

  return (
    <MenuItem onClick={handleDownload} className="msMenuItem">
      <i className="msIcons">
      <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 16 16" height="24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1"></path><path d="M4.603 12.087a.8.8 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.7 7.7 0 0 1 1.482-.645 20 20 0 0 0 1.062-2.227 7.3 7.3 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.187-.012.395-.047.614-.084.51-.27 1.134-.52 1.794a11 11 0 0 0 .98 1.686 5.8 5.8 0 0 1 1.334.05c.364.065.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.86.86 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.7 5.7 0 0 1-.911-.95 11.6 11.6 0 0 0-1.997.406 11.3 11.3 0 0 1-1.021 1.51c-.29.35-.608.655-.926.787a.8.8 0 0 1-.58.029m1.379-1.901q-.25.115-.459.238c-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361q.016.032.026.044l.035-.012c.137-.056.355-.235.635-.572a8 8 0 0 0 .45-.606m1.64-1.33a13 13 0 0 1 1.01-.193 12 12 0 0 1-.51-.858 21 21 0 0 1-.5 1.05zm2.446.45q.226.244.435.41c.24.19.407.253.498.256a.1.1 0 0 0 .07-.015.3.3 0 0 0 .094-.125.44.44 0 0 0 .059-.2.1.1 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a4 4 0 0 0-.612-.053zM8.078 5.8a7 7 0 0 0 .2-.828q.046-.282.038-.465a.6.6 0 0 0-.032-.198.5.5 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822q.036.167.09.346z"></path></svg>
      </i>
      {/* <img src={PDFIMG} alt="PDF" className="msIcons" /> */}
      <span className='msIcons'>PDF</span>
    </MenuItem>
  );
};

export default PDFDownloadButton;
